import type { PesAnalyticsType, Product } from './types/analytics';
import type { Values } from './types/common';
import {
  productLists,
  addCartLocation,
  addCartCTAType,
  virtualPagesTypes,
  virtualPagesNames,
  NUMERIC_SEPARATOR,
} from './constants';

import type { AddToCartEventParams, PageViewEventParams } from './events';
import {
  getLookupValueAddCartCTAType,
  getLookupValueAddCartLocation,
  getLookupValueProductLists,
  getLookupValueVirtualPageType,
  getLookupValueVirtualPageName,
} from './lookups';

declare global {
  interface Window {
    pesAnalytics: PesAnalyticsType;
    analyticsDisabled?: boolean;
  }
}
type AddToCartLocationParam = Values<typeof productLists> | Values<typeof addCartLocation>;

type CtaTypeParam = Values<typeof addCartCTAType>;

export const getAddToCartLocation = (
  params: AddToCartEventParams,
  location?: AddToCartLocationParam,
  ctaType?: CtaTypeParam,
): string => {
  const { relatedSectionId = '' } = params;

  const locationValue = getLookupValueProductLists(location, relatedSectionId)
    || getLookupValueAddCartLocation(location, relatedSectionId);
  const ctaTypeValue = getLookupValueAddCartCTAType(ctaType);

  return `${locationValue}|${ctaTypeValue}`;
};

type VirtualPageTypeParam = Values<typeof virtualPagesTypes>;
type VirtualPageNameParam = Values<typeof virtualPagesNames>;

export const getPageSubSubCategory = (
  params: PageViewEventParams,
  virtualPageType: VirtualPageTypeParam,
  virtualPageName?: VirtualPageNameParam,
): string => {
  const { relatedSectionId = '', mainDocumentType = '' } = params || {};
  const virtualPageTypeValue = getLookupValueVirtualPageType(virtualPageType);
  const virtualPageNameValue = getLookupValueVirtualPageName(
    virtualPageName || '',
    relatedSectionId || mainDocumentType,
  );
  return virtualPageType === virtualPagesTypes.MODAL_WINDOW
    ? `${virtualPageTypeValue}|${virtualPageNameValue}`
    : virtualPageTypeValue;
};

export const removeAllCommas = (input: string): string => input.replace(/,/g, '');

export const removeAllDots = (input: string): string => input.replace(/\./g, '');

/**
 * Returns a numeric price and replaces all COMMA with DOT
 * eg '125,876,454.5' => 12665876454.5
 *    '12121,151' => 12121.151
 */
export const priceToFloat = (price: string | number): number => {
  const regExp = /^[0-9]+(,+[0-9]+)+(\\.[0-9]+)$/;
  const newPrice = price.toString();
  return regExp.test(newPrice)
    ? parseFloat(removeAllCommas(newPrice))
    : parseFloat(newPrice.replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

export const convertProductsPriceToFloat = (products: Product[]): Product[] => {
  return products.map((product) => {
    const { price, ...rest } = product;
    if (!price) {
      return product;
    }
    return {
      price: priceToFloat(price),
      ...rest,
    };
  });
};

export const setActualNoParam = (url: string, pageNoParam: number): string => {
  const noParamRegExp = /No=\d+/;
  const noParamReplacement = `No=${pageNoParam}`;

  if (url.includes('No=')) {
    return url.replace(noParamRegExp, noParamReplacement);
  }
  return `${url}${url.includes('?') ? '&' : '?'}${noParamReplacement}`;
};

export const extractThreeConsecutiveDigits = (input: string) => /^[0-9]{3}\b/.exec(input);

type HandleBothCommaAndDotParams = {
  firstMatch: string;
  firstCommaIndex: number;
  firstDotIndex: number;
  lastCommaIndex: number;
  lastDotIndex: number;
};
export const handleBothCommaAndDot = ({
  firstMatch,
  firstCommaIndex,
  firstDotIndex,
  lastCommaIndex,
  lastDotIndex,
}: HandleBothCommaAndDotParams) => {
  if (firstCommaIndex < firstDotIndex) {
    if (lastCommaIndex > lastDotIndex || lastDotIndex > firstDotIndex) {
      return NaN;
    }
    return Number(removeAllCommas(firstMatch));
  }

  if (lastCommaIndex < lastDotIndex || lastCommaIndex > firstCommaIndex) {
    return NaN;
  }
  return Number(removeAllDots(firstMatch).replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

export const handleOnlyDot = (
  firstMatch: string,
  firstDotIndex: number,
  lastDotIndex: number,
) => {
  const threeDigits = extractThreeConsecutiveDigits(firstMatch.substring(firstDotIndex + 1));
  if (lastDotIndex > firstDotIndex || threeDigits) {
    return Number(removeAllDots(firstMatch));
  }
  return Number(firstMatch);
};

export const handleOnlyComma = (
  firstMatch: string,
  firstCommaIndex: number,
  lastCommaIndex: number,
) => {
  const threeDigits = extractThreeConsecutiveDigits(firstMatch.substring(firstCommaIndex + 1));
  return Number((lastCommaIndex > firstCommaIndex || threeDigits)
    ? removeAllCommas(firstMatch)
    : firstMatch.replace(NUMERIC_SEPARATOR.COMMA, NUMERIC_SEPARATOR.DOT));
};

/**
 * The `parseFloatI18n` function is used to parse and convert a localized number string into a valid
 * JavaScript number. It handles different number string formats that may include commas or dots as decimal
 * separators, and it ensures that the number is correctly formatted for parsing.
 */
export const parseFloatI18n = (input: string) => {
  const ZERO_INDEX = 0;
  if (typeof input !== 'string' || input === '') {
    return typeof input === 'number' ? input : NaN;
  }

  const numericMatches = /^[0-9.,eE-\s]+$/.exec(input);

  if (!numericMatches) {
    return parseFloat(input);
  }

  const [ numericMatch ] = numericMatches;
  const firstMatch = numericMatch.replace(/\s+/g, '');

  const firstCommaIndex = firstMatch.indexOf(NUMERIC_SEPARATOR.COMMA);
  const firstDotIndex = firstMatch.indexOf(NUMERIC_SEPARATOR.DOT);
  const lastCommaIndex = firstMatch.lastIndexOf(NUMERIC_SEPARATOR.COMMA);
  const lastDotIndex = firstMatch.lastIndexOf(NUMERIC_SEPARATOR.DOT);

  if (firstCommaIndex > ZERO_INDEX && firstDotIndex > ZERO_INDEX) {
    return handleBothCommaAndDot({
      firstMatch,
      firstCommaIndex,
      firstDotIndex,
      lastCommaIndex,
      lastDotIndex,
    });
  }

  if (firstDotIndex > ZERO_INDEX && firstCommaIndex < ZERO_INDEX) {
    return handleOnlyDot(firstMatch, firstDotIndex, lastDotIndex);
  }

  if (firstCommaIndex > ZERO_INDEX && firstDotIndex < ZERO_INDEX) {
    return handleOnlyComma(firstMatch, firstCommaIndex, lastCommaIndex);
  }

  if (firstCommaIndex === ZERO_INDEX || firstDotIndex === ZERO_INDEX) {
    return NaN;
  }

  return Number(firstMatch);
};
